import { Col, Empty, Row } from 'antd';
import Avatar from 'antd/lib/avatar/avatar';
import AddressFormatComponent from 'components/common/AddressFormatComponent';
import { EmptyDataContainerStyled } from 'components/common/EmptyData';
import HeaderWithLine from 'components/HeaderWithLine';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { DataFieldProps } from 'components/InputFieldsGenerator/types';
import { DEFAULT_FORMAT_DATE_US, formatDateTimeForDisplay } from 'constants/days';
import moment from 'moment';
import React from 'react';
import { TextOnlyStyled } from 'routes/Transaction/components/styled';
import { AssignmentStatus } from 'types/appointmentAssignments';
import { AppointmentStatus, IAppointment } from 'types/appointments';
import { appointmentTypeOptionsArray } from 'types/transaction';
import { getDefaultAvatarUrl, prependZero } from 'utils';

import { NestedScheduleContainerStyled, ScheduleHeaderStyled } from './styled';
import { ITransactionListTableProps } from './types';

const NestedScheduleList: React.FC<{ data: ITransactionListTableProps }> = ({ data }) => {
  const { transaction } = data;
  const generateScheduleCard = (schedule: IAppointment, index: number) => {
    const signedAddressObject = schedule.address;
    const { notary } = schedule;
    const type = appointmentTypeOptionsArray.find((sc) => sc.value === schedule?.type)?.label || '';
    const signatories = schedule.signatoryIds.reduce((acc: any[], sigId: string) => {
      const signatory = transaction?.signatories.find((sig) => sig.id === sigId);
      if (signatory) {
        acc.push(`${signatory.firstName} ${signatory.lastName}`);
      }
      return acc;
    }, []);
    const transactionDuration = schedule.duration;
    const scheduleSignedTimeStart = schedule.date ? moment(schedule.date) : undefined;
    const scheduleSignedTimeEnd = scheduleSignedTimeStart
      ? scheduleSignedTimeStart.clone().add(transactionDuration, 's')
      : undefined;
    const isAppointmentRequested =
      schedule.assignments && schedule.assignments.some((assignment) => assignment.status === AssignmentStatus.PENDING);

    const scheduleDataField: DataFieldProps[] = [
      {
        label: 'Signatories',
        value: signatories,
        sideFields: [
          {
            label: 'Type',
            value: type,
            size: 8,
          },
          {
            label: 'Notary',
            value: (
              <Row className='gx-m-0'>
                {notary && (
                  <Row align='middle'>
                    <Col flex='32'>
                      <Avatar
                        src={
                          notary.avatar?.thumbnailPipeFrom ||
                          getDefaultAvatarUrl(`${notary?.firstName} ${notary?.lastName}`)
                        }
                      />
                    </Col>
                    <Col>
                      <TextOnlyStyled className='gx-m-0 gx-pl-1' $color='#545454'>
                        {notary?.firstName} {notary?.lastName}
                      </TextOnlyStyled>
                    </Col>
                  </Row>
                )}
                {!notary && (
                  <Col className='gx-pl-0 gx-mr-2'>
                    {isAppointmentRequested ? (
                      <TextOnlyStyled $color='#038fde'>Waiting for Notary approval</TextOnlyStyled>
                    ) : (
                      <TextOnlyStyled $color='#F7CB73'>Awaiting Notary Assignment</TextOnlyStyled>
                    )}
                  </Col>
                )}
              </Row>
            ),
            size: 8,
          },
        ],
      },
      {
        label: 'Address',
        value: <AddressFormatComponent addressDetail={signedAddressObject} separateLines />,
        sideFields: [
          {
            label: 'Start Time',
            value: scheduleSignedTimeStart
              ? scheduleSignedTimeStart.format(formatDateTimeForDisplay('-', DEFAULT_FORMAT_DATE_US))
              : 'None',
            size: 8,
          },
          {
            label: 'End Time',
            value: scheduleSignedTimeEnd
              ? scheduleSignedTimeEnd.format(formatDateTimeForDisplay('-', DEFAULT_FORMAT_DATE_US))
              : 'None',
            size: 8,
          },
        ],
      },
    ];

    let appointmentStatusColorClassname = '';
    switch (schedule.status) {
      case AppointmentStatus.OPEN:
        appointmentStatusColorClassname = 'text-title-subtext';
        break;
      case AppointmentStatus.APPROVED:
        appointmentStatusColorClassname = 'text-active-color';
        break;
      case AppointmentStatus.REQUESTED:
        appointmentStatusColorClassname = 'text-secondary-color';
        break;
      case AppointmentStatus.COMPLETED:
        appointmentStatusColorClassname = 'text-primary-color';
        break;
      case AppointmentStatus.CANCELLED:
        appointmentStatusColorClassname = 'text-gray-500';
        break;
      default:
        break;
    }
    return (
      <NestedScheduleContainerStyled key={index} style={{ cursor: 'default' }}>
        <HeaderWithLine
          style={{
            marginBottom: 30,
          }}>
          <ScheduleHeaderStyled>Appointment {prependZero(index + 1)}</ScheduleHeaderStyled>
          <span className={`ml-3 italic font-medium ${appointmentStatusColorClassname}`}>{schedule.status}</span>
        </HeaderWithLine>
        <div className='gx-px-2'>
          <InputFieldsGenerator dataFields={scheduleDataField} />
        </div>
      </NestedScheduleContainerStyled>
    );
  };

  return (
    <div>
      {/* <Table pagination={false} columns={nestedScheduleTableColumns} dataSource={dataMapper} /> */}
      {(transaction?.appointment?.length || 0) > 0 ? (
        transaction?.appointment?.map(generateScheduleCard)
      ) : (
        <EmptyDataContainerStyled style={{ background: 'transparent' }}>
          <Empty description='No Appointments Created for This Transaction' image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </EmptyDataContainerStyled>
      )}
    </div>
  );
};

export default React.memo(NestedScheduleList);
